/**
 * Counter-Strike-style pointer-lock controller. Click the canvas → cursor
 * locks, mouse motion is reported as raw `(movementX, movementY)` deltas to a
 * registered drag callback, ESC unlocks.
 *
 * Coexists with {@link MouseHandler}'s right-drag pointer-lock gesture without
 * double-rotating: this controller only treats itself as "locked" when its
 * own request was honored. The `pointerlockchange` event fires for any element
 * acquiring lock, so without the `didRequest` gate we'd latch on MouseHandler's
 * right-drag too — and then both controllers' mousemove handlers would fire
 * for every movement event, doubling the camera rotation.
 *
 * Listeners are installed with `{ passive: false }` to mirror the rest of the
 * input layer (see MouseHandler) — a few of them call `preventDefault()`.
 */
export type DragCallback = (dx: number, dy: number) => void;
export type LockChangeCallback = (locked: boolean) => void;

export class PointerLockController {
    private el: HTMLElement;
    private dragCb: DragCallback | null = null;
    private lockSubscribers: LockChangeCallback[] = [];
    private locked = false;
    /** True between our `requestPointerLock()` call and the matching unlock. */
    private didRequest = false;

    private onCanvasClick: (e: MouseEvent) => void;
    private onDocMouseMove: (e: MouseEvent) => void;
    private handleLockChange: () => void;
    private handleLockError: () => void;
    private handleVisibilityChange: () => void;

    constructor(domElement: HTMLElement) {
        this.el = domElement;

        this.onCanvasClick = (e: MouseEvent) => {
            // Plain left-click on the canvas, no modifier keys, while not
            // already locked. Right/middle clicks are owned by MouseHandler's
            // orbit gesture; modifier keys are reserved for hold-to-look.
            if (e.button !== 0) return;
            if (e.ctrlKey || e.altKey || e.metaKey || e.shiftKey) return;
            if (e.target !== this.el) return;
            if (this.locked || this.didRequest) return;
            this.didRequest = true;
            const lockRequest = this.el.requestPointerLock?.();
            if (lockRequest && typeof (lockRequest as Promise<void>).catch === 'function') {
                (lockRequest as Promise<void>).catch(() => { this.didRequest = false; });
            }
        };

        this.onDocMouseMove = (e: MouseEvent) => {
            if (!this.locked) return;
            const dx = e.movementX ?? 0;
            const dy = e.movementY ?? 0;
            if (dx === 0 && dy === 0) return;
            this.dragCb?.(dx, dy);
        };

        this.handleLockChange = () => {
            const browserLocked = document.pointerLockElement === this.el;
            // Only consider ourselves locked when WE requested the lock. If
            // MouseHandler's right-drag acquired it, browserLocked is true but
            // didRequest is false → we stay inactive and don't fire mousemove
            // alongside MouseHandler.
            const nowLocked = browserLocked && this.didRequest;
            if (!browserLocked) this.didRequest = false;
            if (nowLocked !== this.locked) this.setLocked(nowLocked);
        };

        this.handleLockError = () => {
            console.warn('[PointerLockController] pointerlockerror — resetting state');
            this.didRequest = false;
            if (this.locked) this.setLocked(false);
        };

        this.handleVisibilityChange = () => {
            if (typeof document === 'undefined') return;
            if (document.hidden && this.locked) {
                document.exitPointerLock?.();
            }
        };
    }

    onDrag(cb: DragCallback | null): void { this.dragCb = cb; }

    /**
     * Subscribe to lock-state transitions. Multiple subscribers are supported
     * so unrelated UI surfaces (e.g. the crosshair) can react without sharing
     * a single callback slot.
     */
    onLockChange(cb: LockChangeCallback): void { this.lockSubscribers.push(cb); }

    isLocked(): boolean { return this.locked; }

    private setLocked(next: boolean): void {
        this.locked = next;
        for (const cb of this.lockSubscribers) cb(next);
    }

    attach(): void {
        this.el.addEventListener('click', this.onCanvasClick as EventListener, { passive: false });
        document.addEventListener('mousemove', this.onDocMouseMove as EventListener, { passive: false });
        document.addEventListener('pointerlockchange', this.handleLockChange, { passive: false });
        document.addEventListener('pointerlockerror', this.handleLockError, { passive: false });
        document.addEventListener('visibilitychange', this.handleVisibilityChange, { passive: false });
    }

    detach(): void {
        this.el.removeEventListener('click', this.onCanvasClick as EventListener);
        document.removeEventListener('mousemove', this.onDocMouseMove as EventListener);
        document.removeEventListener('pointerlockchange', this.handleLockChange);
        document.removeEventListener('pointerlockerror', this.handleLockError);
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        if (this.locked && document.pointerLockElement === this.el) {
            document.exitPointerLock?.();
        }
        this.locked = false;
        this.didRequest = false;
    }
}
